export interface Rgba {
  width: number;
  height: number;
  data: Uint8ClampedArray;
}

const ICON_SIZE = 16;
const VISIBLE_ALPHA = 32;
const BACKGROUND_TOLERANCE = 40;

export function removeSolidBackground(img: Rgba): Rgba {
  if (hasTransparency(img)) return img;
  const { width, height } = img;
  const data = new Uint8ClampedArray(img.data);
  const background = data.slice(0, 3);
  const visited = new Uint8Array(width * height);
  const stack: number[] = [];
  for (let x = 0; x < width; x++) stack.push(x, (height - 1) * width + x);
  for (let y = 0; y < height; y++) stack.push(y * width, y * width + width - 1);
  while (stack.length) {
    const i = stack.pop()!;
    if (visited[i] || !isNear(data, i * 4, background)) continue;
    visited[i] = 1;
    data[i * 4 + 3] = 0;
    const x = i % width;
    if (x > 0) stack.push(i - 1);
    if (x < width - 1) stack.push(i + 1);
    if (i >= width) stack.push(i - width);
    if (i < width * (height - 1)) stack.push(i + width);
  }
  return { width, height, data };
}

export function pixelate(img: Rgba): Rgba {
  const out = new Uint8ClampedArray(ICON_SIZE * ICON_SIZE * 4);
  const box = squareBounds(img);
  if (!box) return { width: ICON_SIZE, height: ICON_SIZE, data: out };
  const cell = box.size / ICON_SIZE;
  for (let cy = 0; cy < ICON_SIZE; cy++) {
    for (let cx = 0; cx < ICON_SIZE; cx++) {
      const color = averageCell(img, box.x + cx * cell, box.y + cy * cell, cell);
      if (color) out.set(color, (cy * ICON_SIZE + cx) * 4);
    }
  }
  return { width: ICON_SIZE, height: ICON_SIZE, data: out };
}

function averageCell(img: Rgba, x0: number, y0: number, size: number): [number, number, number, number] | null {
  let r = 0, g = 0, b = 0, visible = 0, total = 0;
  for (let y = Math.floor(y0); y < Math.ceil(y0 + size); y++) {
    for (let x = Math.floor(x0); x < Math.ceil(x0 + size); x++) {
      total++;
      if (x < 0 || y < 0 || x >= img.width || y >= img.height) continue;
      const i = (y * img.width + x) * 4;
      if (img.data[i + 3] < VISIBLE_ALPHA) continue;
      visible++;
      r += img.data[i];
      g += img.data[i + 1];
      b += img.data[i + 2];
    }
  }
  if (visible * 2 < total) return null;
  return [Math.round(r / visible), Math.round(g / visible), Math.round(b / visible), 255];
}

function squareBounds(img: Rgba): { x: number; y: number; size: number } | null {
  let minX = img.width, minY = img.height, maxX = -1, maxY = -1;
  for (let y = 0; y < img.height; y++) {
    for (let x = 0; x < img.width; x++) {
      if (img.data[(y * img.width + x) * 4 + 3] < VISIBLE_ALPHA) continue;
      minX = Math.min(minX, x); maxX = Math.max(maxX, x);
      minY = Math.min(minY, y); maxY = Math.max(maxY, y);
    }
  }
  if (maxX < 0) return null;
  const size = Math.max(maxX - minX + 1, maxY - minY + 1);
  return { x: (minX + maxX + 1 - size) / 2, y: (minY + maxY + 1 - size) / 2, size };
}

function hasTransparency(img: Rgba): boolean {
  for (let i = 3; i < img.data.length; i += 4) if (img.data[i] < 255) return true;
  return false;
}

function isNear(data: Uint8ClampedArray, i: number, color: Uint8ClampedArray): boolean {
  return Math.abs(data[i] - color[0]) + Math.abs(data[i + 1] - color[1]) + Math.abs(data[i + 2] - color[2]) <= BACKGROUND_TOLERANCE;
}
